import { AxiosError } from 'axios';
import { find } from 'lodash';
import React, { useCallback, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { binsApi } from 'api';
import {
  DataGridColumnDefinition,
  CrudPage,
  CloseFormikDialogData,
  dataGridColumns,
  CrudTableActionType,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Bin, FilterDefinition } from 'types';
import { validationUtils } from 'utils';

import { BankBinDetailsDialog } from './BankBinDetailsDialog';

type BankBinFilters = {
  id: string;
  bin: string;
  bankId: string;
};

export const BankBinsList: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.bins',
  });

  const { banks } = useUserContext();

  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(QueryKey.Bins, binsApi.getAllPaginated);

  const { mutate: remove } = useMutation(binsApi.remove, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.Bins);
    },
    notifierType: 'remove',
  });

  const { mutate: update } = useMutation<
    Bin,
    AxiosError,
    { id: string; data: Partial<Bin> }
  >(binsApi.update);

  const columns: DataGridColumnDefinition<Bin>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.bank'),
        valueKey: 'bankId',
        valueFormatter: (bankId) => find(banks, { id: bankId })?.name,
      },
      {
        header: t('fields.bin'),
        valueKey: 'bin',
      },
    ],
    [t, banks],
  );

  const handleUpdate = useCallback(
    (
      item: Bin,
      data: CloseFormikDialogData<Partial<Bin>>,
      closeDialog: () => void,
    ) => {
      update(
        { id: item.id, data: data?.values },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.Bins);
            closeDialog();
          },
          onError: (error: AxiosError) => {
            data?.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        },
      );
    },
    [queryClient, update],
  );

  const filtersDefinitions: FilterDefinition<BankBinFilters>[] = useMemo(
    () => [
      {
        label: t('fields.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('fields.bin'),
        name: 'bin',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('fields.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Bank,
      },
    ],
    [t],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          columns,
          paginated: true,
          actions: [
            {
              type: CrudTableActionType.Details,
              renderDialog: BankBinDetailsDialog,
              onUpdate: handleUpdate,
            },
            {
              type: CrudTableActionType.Remove,
              onRemove: (item: Bin, { close }) =>
                remove(item.id, { onSuccess: close }),
            },
          ],
        }}
      />
    </Fragment>
  );
};
